import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import WhitelistManager from './WhitelistManager';
import ApiKeyManager from './ApiKeyManager';
import AccountManagement from './AccountManagement';
import NotificationCenter from './NotificationCenter';
import UserPreferences from './UserPreferences';
import { Settings, Key, Users, Bell, User } from 'lucide-react';
import './AdminPage.css';

const TABS = {
  SETTINGS: 'settings',
  ACCOUNT: 'account',
  API_KEYS: 'api-keys',
  NOTIFICATIONS: 'notifications',
  USER_PREFERENCES: 'user-preferences',
};

function AdminPage({ accountId, onAccountChange, onAccountCreated, initialTab, showCreateAccount, suggestedAccountName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [openCreateModal, setOpenCreateModal] = useState(showCreateAccount || false);

  // Get active tab from URL path - memoized to prevent unnecessary recalculations
  const activeTab = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('/admin/account')) return TABS.ACCOUNT;
    if (path.includes('/admin/api-keys')) return TABS.API_KEYS;
    if (path.includes('/admin/notifications')) return TABS.NOTIFICATIONS;
    if (path.includes('/admin/user-preferences')) return TABS.USER_PREFERENCES;
    if (path.includes('/admin/settings')) return TABS.SETTINGS;
    // Default: if just /admin, default to settings
    return TABS.SETTINGS;
  }, [location.pathname]);

  // Handle initialTab changes (e.g., when "Manage account" is clicked from dropdown)
  // Use useCallback to stabilize the navigate call
  const handleInitialTab = useCallback(() => {
    if (initialTab) {
      navigate(`/admin/${initialTab === 'account' ? 'account' : initialTab}`, { replace: true });
    }
  }, [initialTab, navigate]);

  useEffect(() => {
    handleInitialTab();
  }, [handleInitialTab]);

  // Handle showCreateAccount prop changes
  useEffect(() => {
    if (showCreateAccount) {
      setOpenCreateModal(true);
    }
  }, [showCreateAccount]);

  return (
    <div className="admin-page">
      <div className="admin-tabs">
        <NavLink 
          to="/admin/settings"
          className={({ isActive }) => isActive || activeTab === TABS.SETTINGS ? 'active' : ''}
        >
          <Settings size={18} />
          <span>Settings</span>
        </NavLink>
        <NavLink 
          to="/admin/account"
          className={({ isActive }) => isActive || activeTab === TABS.ACCOUNT ? 'active' : ''}
        >
          <Users size={18} />
          <span>Account</span>
        </NavLink>
        <NavLink 
          to="/admin/api-keys"
          className={({ isActive }) => isActive || activeTab === TABS.API_KEYS ? 'active' : ''}
        >
          <Key size={18} />
          <span>API Keys</span>
        </NavLink>
        <NavLink 
          to="/admin/notifications"
          className={({ isActive }) => isActive || activeTab === TABS.NOTIFICATIONS ? 'active' : ''}
        >
          <Bell size={18} />
          <span>Notifications</span>
        </NavLink>
        <NavLink 
          to="/admin/user-preferences"
          className={({ isActive }) => isActive || activeTab === TABS.USER_PREFERENCES ? 'active' : ''}
        >
          <User size={18} />
          <span>User Preferences</span>
        </NavLink>
      </div>

      <div className="admin-content">
        {activeTab === TABS.SETTINGS && (
          <div className="settings-view">
            <WhitelistManager />
          </div>
        )}

        {activeTab === TABS.ACCOUNT && (
          <div className="account-view">
            <AccountManagement
              accountId={accountId}
              onAccountChange={onAccountChange}
              onAccountCreated={onAccountCreated}
              embedded={true}
              openCreateModal={openCreateModal}
              suggestedAccountName={suggestedAccountName}
            />
          </div>
        )}

        {activeTab === TABS.API_KEYS && (
          <div className="api-keys-view">
            <ApiKeyManager />
          </div>
        )}

        {activeTab === TABS.NOTIFICATIONS && (
          <div className="notifications-view">
            <NotificationCenter />
          </div>
        )}

        {activeTab === TABS.USER_PREFERENCES && (
          <div className="user-preferences-view">
            <UserPreferences />
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPage;
